import { useQuery } from '@tanstack/react-query';
import { playlistPageForIndex, type EmbedList } from '@visious/core';
import { fetchPlaylistPage } from '../../api/playlists';
import { getPlatform } from '../../platform/instance';
import { useCopy } from '../../strings/useCopy';

/**
 * The playlist behind a list embed — **one page of it, the page the entry being played is on**.
 *
 * This is deliberately not `useWatchPlaylist`. That hook reads the whole list into the queue so the
 * side panel can draw it, and an embed has no panel, no queue and no room for either: what it needs
 * is the id of the entry either side of this one and how long the list is. One page answers both,
 * and the page is the one `playlistPageForIndex` puts the current entry in.
 *
 * Two API traps are load-bearing here and both are `docs/api/playlists.md`:
 *
 * - **A page's rows overlap its neighbours' and repeat inside the page itself**, so the rows are read
 *   into a map keyed by `index` — the one key measured to have no conflicts — rather than used as a
 *   list. That is also why {@link EmbedListRead.entries} is a map and not an array: a row's position
 *   in the response says nothing about its position in the playlist.
 * - **A `page` outside the range empties the list at `200`**, so an index past the end answers with a
 *   header and no rows rather than an error. That reads as an empty map, which is the same answer as
 *   "the neighbour is on another page": the step then names the entry by its position and the next
 *   screen resolves it.
 */
export interface EmbedListRead {
  /** The playlist's own `videoCount`, or `0` while nothing has arrived. */
  readonly total: number;
  /** `index` → video id, for the entries on the page in hand. */
  readonly entries: ReadonlyMap<number, string>;
  /** The lowest index on that page, which is where a list with no entry named starts. */
  readonly firstIndex: number | null;
  readonly loading: boolean;
  /** One sentence, or `null`. A list that cannot be read still plays the video it is on. */
  readonly problem: string | null;
}

const EMPTY: ReadonlyMap<number, string> = new Map();

export function useEmbedList(list: EmbedList | null): EmbedListRead {
  const words = useCopy().text.watch.page;
  const page = playlistPageForIndex(list?.index ?? 0);

  const query = useQuery({
    // The page number is in the key: two entries of one playlist that sit on different pages are
    // two reads, and the one in hand is always the one the playing entry is on.
    queryKey: ['embedList', list?.playlistId ?? '', page],
    enabled: list !== null,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (list === null) return null;
      const boot = await getPlatform();
      if (!boot.ok) throw new Error('instance unavailable');
      const result = await fetchPlaylistPage(boot.platform, list.playlistId, page);
      if (!result.ok) throw new Error(result.error.kind);
      return result.value;
    },
  });

  const playlist = query.data ?? null;
  if (playlist === null) {
    return {
      total: 0,
      entries: EMPTY,
      firstIndex: null,
      loading: list !== null && query.isPending,
      problem: query.isError ? words.listProblem : null,
    };
  }

  const entries = new Map<number, string>();
  for (const entry of playlist.entries) entries.set(entry.index, entry.video.videoId);
  const indices = [...entries.keys()];

  return {
    // `videoCount` is what the header reports and `entries.length` is what one page happened to
    // carry, so the header wins — a step off the end of a page is not a step off the end of a list.
    // Where the header has no number at all, the rows in hand are the only answer there is.
    total: playlist.videoCount ?? entries.size,
    entries,
    firstIndex: indices.length === 0 ? null : Math.min(...indices),
    loading: false,
    problem: null,
  };
}
